import React from "react";
import { formatPrice } from "../../utils/formatPrice";
import { Grid, Typography, Box, Collapse, Skeleton } from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import MapBoxComp from "../MapBoxComp";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import AvailabilityWidget from "./AvailabilityWidget";
import TemperatureChart from "./TemperatureWidget";
import EquipmentsModal from "./EquipmentsModal";
import { fetchWeather } from "../../utils/boatApi";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { getEquipmentIcon } from "../../utils/equipmentIcons";
import BookingWidget from "./BookingWidget";

interface FormDataType {
  checkIn: string;
  checkOut: string;
  name: string;
  surname: string;
  email: string;
  telephone: string;
  notes: string;
}
interface YachtInfoProps {
  year: number;
  cabins: number;
  beds: number;
  guests: number;
  bathrooms: number;
  amenities: string[];
  engine: string;
  water_capacity: number;
  sail_type: string;
  fuel_capacity: number;
  length: number;
  services: [] | any;
  yachtAvailability: [];
  additionalServices: any[];
  location: any;
  id: any;
  price: string | number;
  discountPercentage: string | number;
  setFormData: React.Dispatch<React.SetStateAction<FormDataType>>;
  setPrice: React.Dispatch<React.SetStateAction<number | null>>;
  formData: FormDataType;
  startPrice: string | number | null;
  isYachtAvailabilityLoading: boolean;
  setStartPrice: React.Dispatch<React.SetStateAction<number | null>>;
  skipperData: {
    price: number;
    obligatory: boolean;
    name: string;
    unit: string;
  };
  isUnavailable?: boolean;
  isOnOption?: boolean;
  charteredWeeks?: number;
  product?: string;
  actionBar?: React.ReactNode;
}

const YachtInfo: React.FC<YachtInfoProps> = ({
  year,
  cabins,
  beds,
  guests,
  bathrooms,
  amenities,
  engine,
  water_capacity,
  sail_type,
  fuel_capacity,
  length,
  services,
  yachtAvailability,
  additionalServices,
  location,
  id,
  price,
  discountPercentage,
  setFormData,
  setPrice,
  formData,
  startPrice,
  setStartPrice,
  skipperData,
  isYachtAvailabilityLoading,
  isUnavailable,
  isOnOption,
  charteredWeeks,
  product,
  actionBar,
}) => {
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [showLocation, setShowLocation] = React.useState<boolean>(false);
  const [showWeather, setShowWeather] = React.useState<boolean>(false);
  const [showAmenities, setShowAmenities] = React.useState<boolean>(false);
  const [weather, setWeather] = React.useState<any>(null);
  const [weatherLoading, setWeatherLoading] = React.useState(false);

  React.useEffect(() => {
    if (!location?.latitude || !location?.longitude) return;
    let cancelled = false;
    setWeatherLoading(true);
    fetchWeather(location).then((data) => {
      if (!cancelled) {
        setWeather(data);
        setWeatherLoading(false);
      }
    });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.latitude, location?.longitude]);

  // const searchData = useSelector((state: RootState) => state.search);

  // const [similarBoats, setSimilarBoats] = React.useState<any>([]);

  // React.useEffect(() => {

  //   // Extract results
  //   const results = searchData?.searchResults?.results || [];

  //   if (results.length > 0) {
  //     // Filter out the item with the matching ID
  //     const filteredResults = results.filter(
  //       (result: any) => result?.yachtId !== id
  //     );

  //     // Shuffle the filteredResults array
  //     const shuffledResults = filteredResults.sort(() => Math.random() - 0.5);

  //     // Pick the first two items from the shuffled array
  //     const selectedBoats = shuffledResults.slice(0, 3);

  //     // Update the state
  //     setSimilarBoats(shuffledResults);
  //   }
  // }, [searchData, id]);

  const locationStats = {
    ...location,
    longitude: location?.longitude ? location?.longitude : 0,
    latitude: location?.latitude ? location?.latitude : 0,
  };

  return (
    <Box
      sx={{
        mt: 4,
      }}
    >
      <Grid container spacing={4}>
        <Grid item>
          <Typography
            variant="h5"
            sx={{
              px: {
                xs: "2rem",
                md: 0,
              },
            }}
          >
            Description
          </Typography>
          <Grid
            container
            py={3}
            rowSpacing={2}
            columnSpacing={6}
            sx={{
              px: {
                xs: "2rem",
                md: 0,
              },
            }}
          >
            {[
              {
                label: "Year",
                value: year || "N/A",
                icon: `/assets/images/year.svg`,
              },
              {
                label: "Cabins",
                value: cabins || "N/A",
                icon: `/assets/images/cabin.svg`,
              },
              {
                label: "Beds",
                value: beds || "N/A",
                icon: `/assets/images/beds.svg`,
              },
              {
                label: "Bathrooms",
                value: bathrooms || "N/A",
                icon: `/assets/images/bathroom.svg`,
              },
              {
                label: "Engine",
                value: engine || "N/A",
                icon: `/assets/images/engine.svg`,
              },
              {
                label: "Water Tank",
                value: water_capacity || "N/A",
                icon: `/assets/images/watertank.svg`,
              },
              {
                label: "Sail Type",
                value: sail_type || "N/A",
                icon: `/assets/images/sail.svg`,
              },
              {
                label: "Fuel Tank",
                value: fuel_capacity || "N/A",
                icon: `/assets/images/watertank.svg`,
              },
              {
                label: "Length",
                value: length ? `${Math.round(length * 3.28084)} ft` : "N/A",
                icon: `/assets/images/ruler.svg`,
              },
              ...(product ? [{
                label: "Charter Type",
                value: product === "bareboat" ? "Bareboat" : product === "crewed" ? "Crewed" : product,
                icon: `/assets/images/sail.svg`,
              }] : []),
            ].map((item, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Typography
                  variant="body2"
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <img
                      src={item.icon}
                      alt={item.label}
                      style={{
                        width: "20px",
                        height: "20px",
                        objectFit: "contain",
                      }}
                    />
                    {item.label}
                  </span>
                  <span>{item.value}</span>
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Grid>

        {amenities?.length > 0 && (
          <Grid item xs={12}>
            <Typography
              variant="h5"
              sx={{
                px: {
                  xs: "2rem",
                  md: 0,
                },
              }}
            >
              Equipment
            </Typography>
            <Grid
              container
              spacing={2}
              py={3}
              sx={{
                px: {
                  xs: "2rem",
                  md: 0,
                },
              }}
            >
              {amenities.filter((a: any) => a.name?.trim() && !a.name.trim().toLowerCase().includes("unknown")).slice(0, 6).map((amenity: any, index) => (
                <Grid
                  item
                  xs={6}
                  sm={6}
                  md={4}
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Box sx={{ color: "text.secondary", display: "flex", alignItems: "center", flexShrink: 0 }}>
                    {getEquipmentIcon(amenity.name) ?? <CheckCircleOutlineIcon fontSize="small" />}
                  </Box>
                  <Typography variant="body2">
                    {amenity.name}
                  </Typography>
                </Grid>
              ))}
            </Grid>

            <EquipmentsModal
              open={showAmenities}
              onClose={() => setShowAmenities(false)}
              amenities={amenities.filter((a: any) => a.name?.trim() && !a.name.trim().toLowerCase().includes("unknown"))}
            />

            <Box pb={2} borderBottom="1px solid #d3d3d3">
              <Typography
                sx={{
                  color: "#006fff",
                  fontWeight: "700",
                  fontSize: "22px",
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  px: {
                    xs: "2rem",
                    md: 0,
                  },
                }}
                onClick={() => setShowAmenities(!showAmenities)}
              >
                Show all equipment
              </Typography>
            </Box>
          </Grid>
        )}

        {(isYachtAvailabilityLoading || (yachtAvailability && yachtAvailability.length > 0)) && (
          <Grid item xs={12} md={12}>
            <Typography
              variant="h5"
              py={3}
              sx={{
                px: {
                  xs: "2rem",
                  md: 0,
                },
              }}
            >
              Availability
            </Typography>

            <Box px={3}>
              <AvailabilityWidget
                data={yachtAvailability}
                setFormData={setFormData}
                formData={formData}
                setPrice={setPrice}
                setStartPrice={setStartPrice}
                isLoading={isYachtAvailabilityLoading}
              />
            </Box>

          </Grid>
        )}
        {actionBar && (
          <Grid item xs={12}>
            {actionBar}
          </Grid>
        )}

        <Grid item xs={12} md={4}>
          <Box
            sx={{
              display: {
                md: "none",
              },
            }}
          >
            <BookingWidget
              id={id}
              price={price}
              startPrice={startPrice}
              discount={discountPercentage}
              formData={formData}
              setFormData={setFormData}
              skipperData={skipperData}
              isUnavailable={!!isUnavailable}
              isOnOption={!!isOnOption}
              charteredWeeks={charteredWeeks}
            />
          </Box>
        </Grid>
        {/* ADDITIONAL SERVICES */}
        <Grid item xs={12} md={12}>
          <Typography
            variant="h5"
            py={3}
            sx={{
              px: {
                xs: "2rem",
                md: 0,
              },
            }}
          >
            Additional Services
          </Typography>
          <Grid item xs={12} py={3}>
            <Typography
              variant="body2"
              sx={{ color: "gray", px: { xs: "2rem", md: 0 } }}
            >
              Additional services that will be paid directly at check-in.
            </Typography>
          </Grid>

          {services.map((service: any, idx: number) => (
            <Grid
              container
              key={service.id ?? idx}
              spacing={2}
              marginTop={1}
              marginBottom={5}
              sx={{
                px: {
                  xs: "2rem",
                  md: 0,
                },
              }}
            >
              {/* Name and Description Column */}
              <Grid item xs={6} sm={8}>
                <Typography variant="body2">{service.name}</Typography>
                <Typography variant="body2" color="textSecondary">
                  {service.description}
                </Typography>
              </Grid>

              {/* Payable Status Column */}
              <Grid item xs={3} sm={2}>
                {service?.refundable ? (
                  <Typography variant="body2" sx={{ color: "green" }}>
                    Refundable
                  </Typography>
                ) : (
                  <Typography
                    variant="body2"
                    sx={{ color: service.payableInBase ? "red" : "" }}
                  >
                    {service.payableInBase ? "Required" : "Optional"}
                  </Typography>
                )}
              </Grid>

              {/* Price Column */}
              <Grid item xs={3} sm={2}>
                {service.price === 0 || service.price === "0" ? (
                  <Typography variant="body2" sx={{ color: "green", fontWeight: "bold" }}>
                    Included
                  </Typography>
                ) : (
                  <Typography variant="body2">
                    {formatPrice(service.price)} {(service.unit || "per week").replace(/_/g, " ")}
                  </Typography>
                )}
              </Grid>
            </Grid>
          ))}

          {/* ALL ADDITIONAL SERVICES */}
          <Box
            pb={2}
            borderBottom="1px solid #d3d3d3"
            sx={{
              px: {
                xs: "2rem",
                md: 0,
              },
            }}
          >
            <Typography
              sx={{
                color: "#006fff",
                fontWeight: "700",
                fontSize: "22px",
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
              }}
              onClick={() => setIsOpen(!isOpen)}
            >
              Show all the additional services
              {isOpen ? (
                <ExpandLess sx={{ marginLeft: "8px", fontSize: "24px" }} />
              ) : (
                <ExpandMore sx={{ marginLeft: "8px", fontSize: "24px" }} />
              )}
            </Typography>

            {/* <Grid item xs={12} md={4}>
         
              <MapboxMap
              yachtLocations={yachtLocations}
              hoveredLocation={hoveredLocation}
              />
              </Grid> */}
            <Collapse in={isOpen} sx={{ transition: "all 0.3s ease" }}>
              {additionalServices && additionalServices?.length > 0 ? (
                <>
                  {additionalServices?.map((service: any) => (
                    <Grid container key={service.id} spacing={2} marginTop={1}>
                      {/* Name and Description Column */}
                      <Grid item xs={6} sm={8}>
                        <Typography variant="body2">{service.name}</Typography>
                        <Typography variant="body2" color="textSecondary">
                          {service.description}
                        </Typography>
                      </Grid>

                      {/* Payable Status Column */}
                      <Grid item xs={3} sm={2}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: service.includesDepositWaiver
                              ? "green"
                              : "black",
                          }}
                        >
                          {service.includesDepositWaiver
                            ? "Refundable"
                            : "Optional"}
                        </Typography>
                      </Grid>

                      {/* Price Column */}
                      <Grid item xs={3} sm={2}>
                        {service.price === 0 || service.price === "0" ? (
                          <Typography variant="body2" sx={{ color: "green", fontWeight: "bold" }}>
                            Included
                          </Typography>
                        ) : (
                          <Typography variant="body2">
                            {formatPrice(service.price)} {(service.unit || "per week").replace(/_/g, " ")}
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  ))}
                </>
              ) : (
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ color: "gray", p: 2, textAlign: "center" }}
                  >
                    Additional services not available
                  </Typography>
                </Box>
              )}
            </Collapse>
          </Box>
          {/* BOAT LOCATION */}
          <Box
            py={2}
            borderBottom="1px solid #d3d3d3"
            sx={{
              px: {
                xs: "2rem",
                md: 0,
              },
              display: {
                md: "none",
              },
            }}
          >
            <Typography
              sx={{
                color: "#006fff",
                fontWeight: "700",
                fontSize: "22px",
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
              }}
              onClick={() => setShowLocation(!showLocation)}
            >
              Boat Location
              {showLocation ? (
                <ExpandLess sx={{ marginLeft: "8px", fontSize: "24px" }} />
              ) : (
                <ExpandMore sx={{ marginLeft: "8px", fontSize: "24px" }} />
              )}
            </Typography>

            <Collapse in={showLocation} sx={{ transition: "all 0.3s ease" }}>
              <Box marginTop={2}>
                {location ? (
                  <MapBoxComp
                    location={locationStats}
                    // hoveredLocation={hoveredLocation}
                  />
                ) : (
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ color: "gray", p: 2, textAlign: "center" }}
                    >
                      Location not available
                    </Typography>
                  </Box>
                )}
              </Box>
            </Collapse>
          </Box>
          {/* WEATHER WIDGET */}
          <Box
            py={2}
            borderBottom="1px solid #d3d3d3"
            sx={{
              px: {
                xs: "2rem",
                md: 0,
              },
            }}
          >
            <Typography
              sx={{
                color: "#006fff",
                fontWeight: "700",
                fontSize: "22px",
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
              }}
              onClick={() => setShowWeather(!showWeather)}
            >
              Weather in {location?.name}
              {showWeather ? (
                <ExpandLess sx={{ marginLeft: "8px", fontSize: "24px" }} />
              ) : (
                <ExpandMore sx={{ marginLeft: "8px", fontSize: "24px" }} />
              )}
            </Typography>

            <Collapse in={showWeather} sx={{ transition: "all 0.3s ease" }}>
              <Box marginTop={2}>
                {weatherLoading ? (
                  <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2 }} />
                ) : weather ? (
                  <TemperatureChart
                    weather={weather}
                    country={location?.name}
                  />
                ) : null}
              </Box>
            </Collapse>
          </Box>

          {/* BOAT LOCATION */}
          <Box
            py={2}
            borderBottom="1px solid #d3d3d3"
            sx={{
              px: {
                xs: "2rem",
                md: 0,
              },
              display: {
                xs: "none",
                md: "flow",
              },
            }}
          >
            <Typography
              sx={{
                color: "#006fff",
                fontWeight: "700",
                fontSize: "22px",
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
              }}
              onClick={() => setShowLocation(!showLocation)}
            >
              Boat Location
              {showLocation ? (
                <ExpandLess sx={{ marginLeft: "8px", fontSize: "24px" }} />
              ) : (
                <ExpandMore sx={{ marginLeft: "8px", fontSize: "24px" }} />
              )}
            </Typography>

            <Collapse in={showLocation} sx={{ transition: "all 0.3s ease" }}>
              <Box marginTop={2}>
                {location ? (
                  <MapBoxComp
                    location={locationStats}
                    // hoveredLocation={hoveredLocation}
                  />
                ) : (
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ color: "gray", p: 2, textAlign: "center" }}
                    >
                      Location not available
                    </Typography>
                  </Box>
                )}
              </Box>
            </Collapse>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default YachtInfo;
